import { isRecord } from "@training/shared";
import type { Lift, SessionDetail, SessionStatus } from "@training/shared";

/**
 * Pure helpers over a session's lines, shared by the session list and the
 * movement screen. Kept out of the route files so they stay testable and the
 * route modules export components only.
 */

export type Entry = SessionDetail["exercises"][number];

export function workingSets(entry: Entry) {
  return entry.sets.filter((set) => !set.isWarmup);
}

/**
 * Sets done on this line, wherever they were logged. A session performed
 * outside the app only has the sheet's loads, and they count all the same.
 */
export function doneCount(entry: Entry): number {
  return workingSets(entry).length || entry.sheetSets.length;
}

/** Kilos lifted over a session's working sets, as its screen and the home screen show them. */
export function tonnage(entries: Entry[]): number {
  return entries.reduce(
    (sum, entry) => sum + workingSets(entry).reduce((total, set) => total + set.weightKg * set.reps, 0),
    0,
  );
}

/** The sheet's loads, when they are the only trace of the line. Read-only. */
export function sheetOnlySets(entry: Entry) {
  return workingSets(entry).length === 0 ? entry.sheetSets : [];
}

export function isComplete(entry: Entry): boolean {
  if (entry.doneAt || entry.skippedAt) return true;
  const done = doneCount(entry);
  return entry.targetSets ? done >= entry.targetSets : done > 0;
}

export function describe(entry: Entry): string {
  const done = workingSets(entry).length;
  if (done > 0 && isMyoRep(entry.method)) {
    return `${formatCluster(workingSets(entry))} · ${format(workingSets(entry)[0]!.weightKg)} kg`;
  }
  if (done > 0) {
    const best = workingSets(entry).reduce((a, b) => (b.weightKg > a.weightKg ? b : a));
    return `${done}${entry.targetSets ? `/${entry.targetSets}` : ""} séries · ${format(best.weightKg)} kg`;
  }
  // Performed outside the app: the sheet kept the loads but never the reps.
  if (entry.sheetSets.length > 0) {
    const heaviest = entry.sheetSets.reduce((a, b) => (b.weightKg > a.weightKg ? b : a));
    return `${entry.sheetSets.length} séries · ${format(heaviest.weightKg)} kg · feuille`;
  }
  if (entry.doneAt) return "fait";
  if (entry.skippedAt) return "passé";
  const target = [
    entry.targetSets ? `${entry.targetSets}×` : "",
    entry.targetReps ?? "",
    entry.targetWeightKg ? ` @ ${format(entry.targetWeightKg)} kg` : "",
  ]
    .join("")
    .trim();
  return target || "à saisir";
}

type PreviousSet = NonNullable<Entry["previous"]>["sets"][number];

/** "38,3 kg × 6", "11,3 kg × 8 → 9 kg × 5" for a drop set, "36 kg" when only the sheet knows it. */
export function formatPreviousSet(set: PreviousSet): string {
  return formatSet(set, true);
}

/** A set's load and reps, whatever it carries: sides, a drop, or neither. */
export interface SetFigures {
  weightKg: number;
  dropWeightKg: number | null;
  /** Null when the sheet is the only trace. Left side when one-sided. */
  reps: number | null;
  repsRight?: number | null;
  dropReps?: number | null;
  dropRepsRight?: number | null;
  /** The right side's loads, when they are not the left's. */
  weightKgRight?: number | null;
  dropWeightKgRight?: number | null;
}

/**
 * The second load of a drop set; null when the set is not one. A drop to
 * 0 kg is not one either: the drop's stepper used to open there, and sets
 * were validated with it as it stood ("11,3 → 0", 08/10).
 */
export function dropLoadOf(set: { dropWeightKg: number | null }): number | null {
  return set.dropWeightKg !== null && set.dropWeightKg > 0 ? set.dropWeightKg : null;
}

export type Side = "left" | "right";

export const SIDES: readonly Side[] = ["left", "right"];
export const SIDE_NAME: Record<Side, string> = { left: "gauche", right: "droite" };

export function otherSide(side: Side): Side {
  return side === "left" ? "right" : "left";
}

/** Logged a side at a time: the right has reps of its own, or a load of its own. */
export function hasSides(set: SetFigures): boolean {
  return set.repsRight != null || set.weightKgRight != null;
}

/**
 * One side of a set, as a set with no sides. Whatever the right side was not
 * told apart on is the left's: a load is only stored twice when it differs.
 */
export function sideOf(set: SetFigures, side: Side): SetFigures {
  const dropLoad = dropLoadOf(set);
  if (side === "left") {
    return { weightKg: set.weightKg, dropWeightKg: dropLoad, reps: set.reps, dropReps: set.dropReps ?? null };
  }
  return {
    weightKg: set.weightKgRight ?? set.weightKg,
    dropWeightKg: dropLoad === null ? null : (set.dropWeightKgRight ?? dropLoad),
    reps: set.repsRight ?? set.reps,
    dropReps: set.dropRepsRight ?? set.dropReps ?? null,
  };
}

/**
 * "36 × 8", "8 × 9/8" left/right, "15,8 × 9 → 11,5 × 6" with the reps on
 * each side of the drop, "15,8 → 11,5 × 9" for a drop logged before those
 * were, "G 15,8 × 9 · D 13,6 × 8" when the sides were not loaded alike.
 * `withUnit` puts the kilos in, for a line read on its own.
 */
export function formatSet(set: SetFigures, withUnit = false): string {
  if (set.weightKgRight != null || set.dropWeightKgRight != null) {
    return `G ${formatSet(sideOf(set, "left"), withUnit)} · D ${formatSet(sideOf(set, "right"), withUnit)}`;
  }
  const kg = withUnit ? " kg" : "";
  const reps = set.reps === null ? null : sides(set.reps, set.repsRight ?? null);
  const dropLoad = dropLoadOf(set);
  if (dropLoad !== null && set.dropReps != null) {
    const drop = sides(set.dropReps, set.dropRepsRight ?? null);
    return `${format(set.weightKg)}${kg} × ${reps ?? "?"} → ${format(dropLoad)}${kg} × ${drop}`;
  }
  const load = dropLoad !== null ? `${format(set.weightKg)} → ${format(dropLoad)}` : format(set.weightKg);
  return reps !== null ? `${load}${kg} × ${reps}` : `${load}${kg}`;
}

/** The reps alone, as a set's slot shows them under its load: "9", "9/8", "9 → 6", "9/8 → 6/6". */
export function formatSetReps(set: SetFigures): string {
  const reps = set.reps === null ? "?" : sides(set.reps, set.repsRight ?? null);
  return set.dropReps != null ? `${reps} → ${sides(set.dropReps, set.dropRepsRight ?? null)}` : reps;
}

/**
 * The reps that count for progress: the weaker side's. A left arm at 9 and a
 * right at 8 made 8 clean reps of the movement.
 */
export function countedReps(set: SetFigures): number | null {
  if (set.reps === null) return null;
  return set.repsRight != null ? Math.min(set.reps, set.repsRight) : set.reps;
}

function sides(left: number, right: number | null): string {
  return right === null ? String(left) : `${left}/${right}`;
}

/**
 * Done one side at a time, so logged one side at a time: the coach writes
 * "8/c" (per side), or the name says so. The exercise's own flag covers the
 * rest.
 */
export function isUnilateral(entry: Entry): boolean {
  return (
    entry.exercise.isUnilateral || /unilat/i.test(entry.exercise.name) || /\d\s*\/\s*c\b/i.test(entry.targetReps ?? "")
  );
}

/**
 * The coach's RIR, set by set: "1-1-0-0" is one rep in reserve on sets 1
 * and 2, none after. The sheet's column is read as `tempo` (its header
 * aliases share it), so anything that is not that pattern is not a RIR.
 */
export function rirPlan(entry: Pick<Entry, "tempo" | "targetSets">): number[] {
  const raw = (entry.tempo ?? "").replace(/\s/g, "");
  if (!/^\d+(?:-\d+)*$/.test(raw)) return [];
  const plan = raw.split("-").map(Number);
  // The sheet always has four slots; a three-set line uses three.
  return entry.targetSets ? plan.slice(0, entry.targetSets) : plan;
}

/** The RIR asked for set `setIndex`, or null when the coach gave none for it. */
export function rirFor(entry: Entry, setIndex: number): number | null {
  return rirPlan(entry)[setIndex - 1] ?? null;
}

/** "3×8/c @ 8 kg": the coach's line, empty when he wrote none of it. */
export function targetLabel(entry: Entry): string {
  return [
    entry.targetSets ? `${entry.targetSets}×` : "",
    entry.targetReps ?? "",
    entry.targetWeightKg ? ` @ ${format(entry.targetWeightKg)} kg` : "",
  ]
    .join("")
    .trim();
}

/**
 * The reps a prescription asks for, as a number: the first one written —
 * "8/c" and "8 (9)" ask for 8. Null for "X", to failure, which asks for none.
 */
export function firstRepTarget(reps: string | null): number | null {
  const match = reps?.match(/\d+/);
  return match ? Number.parseInt(match[0], 10) : null;
}

/** The reps being typed for the next set, before and after the drop when it has one. */
export interface RepsDraft {
  reps: number;
  dropReps: number;
}

/** Everything typed for a set, or for one side of it: what the steppers hold. */
export interface SideFigures extends RepsDraft {
  weightKg: number;
  dropWeightKg: number;
}

/**
 * A one-sided set being typed, a side at a time: the side on the steppers,
 * and what was left on them for a side already typed. No side at first — it
 * is picked, not assumed: some sets start on the right, and the next one on
 * the left after the rest.
 */
export interface SidesDraft {
  active: Side | null;
  typed: Partial<Record<Side, SideFigures>>;
}

export const NO_SIDE: SidesDraft = { active: null, typed: {} };

/**
 * Moving the steppers to a side. What they held stays with the side being
 * left; they take back what `to` was given, when it was already typed.
 *
 * A side typed for the first time opens on the steppers as they stand —
 * the other side's figures, most often the same — unless `last`, the set
 * before, loaded the sides differently: then each side picks its own load
 * back up, like any movement picks up its last one.
 */
export function switchSide(
  draft: SidesDraft,
  to: Side,
  current: SideFigures,
  last: SetFigures | undefined,
): { draft: SidesDraft; figures: SideFigures } {
  const typed = draft.active ? { ...draft.typed, [draft.active]: current } : draft.typed;
  return { draft: { active: to, typed }, figures: typed[to] ?? openSide(to, current, last) };
}

function openSide(side: Side, current: SideFigures, last: SetFigures | undefined): SideFigures {
  if (!last || (last.weightKgRight == null && last.dropWeightKgRight == null)) return current;
  const own = sideOf(last, side);
  return { ...current, weightKg: own.weightKg, dropWeightKg: own.dropWeightKg ?? current.dropWeightKg };
}

/** Both sides once each has been on the steppers, the one still there included; null until then. */
export function bothSides(draft: SidesDraft, current: SideFigures): Record<Side, SideFigures> | null {
  const typed = draft.active ? { ...draft.typed, [draft.active]: current } : draft.typed;
  return typed.left && typed.right ? { left: typed.left, right: typed.right } : null;
}

/**
 * What a set saves as: `right` is null on a movement with no sides, and
 * figures that do not apply are null. The right side's loads are only kept
 * when they are not the left's.
 */
export function figuresToSave(left: SideFigures, right: SideFigures | null, dropSet: boolean) {
  const differs = (mine: number, theirs: number) => (mine === theirs ? null : mine);
  // Nothing on the bar after the drop: no drop was done.
  const dropped = dropSet && left.dropWeightKg > 0;
  return {
    weightKg: left.weightKg,
    reps: left.reps,
    dropWeightKg: dropped ? left.dropWeightKg : null,
    dropReps: dropped ? left.dropReps : null,
    repsRight: right ? right.reps : null,
    weightKgRight: right ? differs(right.weightKg, left.weightKg) : null,
    dropRepsRight: right && dropped ? right.dropReps : null,
    dropWeightKgRight: right && dropped ? differs(right.dropWeightKg, left.dropWeightKg) : null,
  };
}

/**
 * Where the reps after the drop start: the drop just done, else last time's
 * for the same set. Null when neither says it.
 */
export function defaultDropReps(entry: Entry, setIndex: number): number | null {
  const done = workingSets(entry).at(-1)?.dropReps;
  if (done != null) return done;
  return previousFor(entry, setIndex)?.dropReps ?? null;
}

/**
 * The number the next set gets. One past the highest already there, not the
 * count: after a deleted set 2, counting would hand out a second "set 3".
 */
export function nextSetIndex(sets: { setIndex: number }[]): number {
  return sets.reduce((max, set) => Math.max(max, set.setIndex), 0) + 1;
}

/** Last time's set with the same number: what set N is read against. */
export function previousFor(entry: Entry, setIndex: number): PreviousSet | null {
  return entry.previous?.sets.find((set) => set.setIndex === setIndex) ?? null;
}

/**
 * Which of last time's sets was its best, the one the load opens on: where
 * the whole list is on screen, it is marked there rather than said twice.
 */
export function previousTopSet(entry: Entry): number | null {
  const top = entry.lastTime;
  if (!top) return null;
  const set = entry.previous?.sets.find((set) => set.weightKg === top.weightKg && set.reps === top.reps);
  return set?.setIndex ?? null;
}

export function isDropSet(method: string | null): boolean {
  return method !== null && /drop/i.test(method);
}

export function isMyoRep(method: string | null): boolean {
  return method !== null && /myo/i.test(method);
}

/** What a myo-rep mini aims for, whatever the activation set reached. */
export const MYO_MINI_REPS = 5;

/**
 * The reps field's starting value for set `setIndex`, or null to keep the
 * last one typed. Past a myo-rep's activation it is a mini: 5, not the 20
 * just done.
 */
export function presetReps(entry: Entry, setIndex: number): number | null {
  return isMyoRep(entry.method) && setIndex > 1 ? MYO_MINI_REPS : null;
}

/**
 * A myo-rep has no set count: the minis go on until failure, which only the
 * lifter can call. Started and not yet called done.
 */
export function isMyoRepRunning(entry: Entry): boolean {
  return isMyoRep(entry.method) && workingSets(entry).length > 0 && entry.doneAt === null;
}

/** "Activation", "Mini 1"...: how a myo-rep's sets read. Null on any other line. */
export function myoSetLabel(entry: Entry, setIndex: number): string | null {
  if (!isMyoRep(entry.method)) return null;
  return setIndex === 1 ? "Activation" : `Mini ${setIndex - 1}`;
}

/** "20 + 5 + 5 + 3 (33)": a myo-rep cluster, and its total. */
export function formatCluster(sets: { reps: number }[]): string {
  const total = sets.reduce((sum, set) => sum + set.reps, 0);
  const cluster = sets.map((set) => set.reps).join(" + ");
  return sets.length > 1 ? `${cluster} (${total})` : cluster;
}

/** The short breath of a myo-rep cluster, and between the two halves of a superset. */
export const SHORT_REST_SECONDS = 20;

/**
 * The countdown after set `setIndex` of this line. A myo-rep takes the
 * prescribed rest after its activation set only, then 20 s between minis. In
 * a superset the pull leaves the full rest on the last movement and 0 on the
 * earlier ones: those get 20 s to move to the partner, not a chained start.
 */
export function restAfterSet(entry: Entry, setIndex: number, defaultRest: number): number {
  if (isMyoRep(entry.method) && setIndex > 1) return SHORT_REST_SECONDS;
  if (entry.supersetGroup !== null && entry.restSeconds === 0) return SHORT_REST_SECONDS;
  return entry.restSeconds ?? defaultRest;
}

/**
 * Where the drop load starts: the drop just done, else last time's for the
 * same set, else the second load the coach wrote ("11,3 - 9").
 */
export function defaultDropWeight(entry: Entry, setIndex: number): number | null {
  const last = workingSets(entry).at(-1);
  const done = last ? dropLoadOf(last) : null;
  if (done !== null) return done;
  const before = previousFor(entry, setIndex);
  const previous = before ? dropLoadOf(before) : null;
  if (previous !== null) return previous;
  const loads = (entry.targetWeightRaw ?? "").match(/\d+(?:[.,]\d+)?/g);
  return loads && loads.length > 1 ? Number.parseFloat(loads[1]!.replace(",", ".")) : null;
}

/**
 * A finished session opens read-only: a set typed there by mistake lands on
 * the wrong day. "Corriger" unlocks it. Enforced here, never on the server — a
 * set typed offline just before finishing still has to land afterwards.
 */
export function canLog(status: SessionStatus, unlocked: boolean): boolean {
  return status !== "done" || unlocked;
}

/** "36 kg", "11,3 → 9 kg": a set's load, both halves of a drop set. */
export function formatLoad(weightKg: number, dropWeightKg: number | null): string {
  return dropWeightKg !== null ? `${format(weightKg)} → ${format(dropWeightKg)} kg` : `${format(weightKg)} kg`;
}

/** A note typed mid-set: blank means none, so an untouched field saves nothing. */
export function cleanNote(input: string): string | null {
  const note = input.replace(/\s+/g, " ").trim();
  return note === "" ? null : note;
}

export function format(value: number): string {
  return String(Math.round(value * 10) / 10).replace(".", ",");
}

/** 105 -> "1'45", 60 -> "1'", 0 -> "enchaîné". */
export function formatRest(seconds: number): string {
  if (seconds === 0) return "enchaîné";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes}'` : `${minutes}'${String(rest).padStart(2, "0")}`;
}

/** 0 -> "01": position in the session, as it is written on the coach's sheet. */
export function ordinal(index: number): string {
  return String(index + 1).padStart(2, "0");
}

export function formatDate(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

/**
 * The sets of this session that were records when lifted: each is judged
 * against the earlier sessions' best and the sets before it today, so a tie
 * with the set just before is not a second record.
 */
export function recordSetIds(entry: Entry): Set<string> {
  const records = new Set<string>();
  const earlier: Lift[] = [...entry.bestLifts];
  for (const set of entry.sets) {
    if (set.isWarmup || set.reps === null) continue;
    const lift = { weightKg: set.weightKg, reps: set.reps };
    if (entry.bestLifts.length > 0 && isRecord(earlier, lift)) records.add(set.clientId);
    earlier.push(lift);
  }
  return records;
}

/**
 * "Dos/Triceps/Epaules" has no space to wrap at: set large, it ran past the
 * phone's edge and the whole page slid sideways. A zero-width space after each
 * slash lets it wrap there rather than mid-word.
 */
export function breakableName(name: string): string {
  return name.replaceAll("/", "/​");
}

/** What the shortcut to today's session says: it stays there once done, as a recap. */
export function sessionLabel(status: SessionStatus): string {
  if (status === "in_progress") return "En cours";
  if (status === "done") return "Faite aujourd'hui";
  return "Prochaine séance";
}

export interface CancelLine {
  id: number;
  name: string;
  /** "6,8 kg × 20, 5, 5", "coché", or null when nothing was recorded. */
  logged: string | null;
}

/**
 * What cancelling a session deletes, line by line, for the confirmation to
 * spell out. `sets` counts warm-ups too: the server deletes them all and
 * checks this exact number before it does.
 */
export function cancelSummary(exercises: Entry[]): { lines: CancelLine[]; sets: number } {
  const lines = exercises.map((entry) => ({
    id: entry.id,
    name: entry.exercise.name,
    logged: entry.sets.length > 0 ? formatSets(entry.sets) : entry.doneAt ? "coché" : null,
  }));
  const sets = exercises.reduce((sum, entry) => sum + entry.sets.length, 0);
  return { lines, sets };
}

/** Consecutive sets at one load share it: "36 kg × 8, 8 · 34 kg × 6". */
function formatSets(sets: Entry["sets"]): string {
  const groups: { load: string; efforts: string[] }[] = [];
  for (const set of [...sets].sort((a, b) => a.setIndex - b.setIndex)) {
    // A plank or a bodyweight move has no load worth reading "0 kg".
    const dropLoad = dropLoadOf(set);
    const load = set.weightKg === 0 && dropLoad === null ? "" : formatLoad(set.weightKg, dropLoad);
    const effort = set.durationSeconds !== null && set.reps === 0 ? `${set.durationSeconds} s` : String(set.reps);
    const last = groups.at(-1);
    if (last?.load === load) last.efforts.push(effort);
    else groups.push({ load, efforts: [effort] });
  }
  return groups.map((group) => (group.load ? `${group.load} × ` : "") + group.efforts.join(", ")).join(" · ");
}

/**
 * "il y a 3 jours" reads faster mid-gym than a date does.
 *
 * Counted in calendar days, local time. Dividing elapsed milliseconds and
 * rounding made the answer depend on the hour: "2026-09-24" parses as UTC
 * midnight, so a session performed yesterday read "hier" in the morning and
 * "il y a 2 jours" by mid-afternoon.
 */
export function sinceLabel(lastPerformedOn: string | null): string {
  if (!lastPerformedOn) return "jamais faite";

  const then = new Date(`${lastPerformedOn}T00:00:00`);
  const today = new Date();
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((midnight.getTime() - then.getTime()) / 86_400_000);

  if (days <= 0) return "faite aujourd'hui";
  if (days === 1) return "hier";
  return `il y a ${days} jours`;
}
